import axios, { AxiosInstance } from 'axios';

declare global {
  interface Window {
    __ADMIN_API_URL__?: string;
  }
}
const API_BASE_URL =
  (typeof window !== 'undefined' && window.__ADMIN_API_URL__) ||
  import.meta.env.VITE_ADMIN_API_URL ||
  '/api';

const client: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

client.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export interface AdminUser {
  id: number;
  email: string;
  mustChangePassword: boolean;
}

export interface AdminAccountListItem {
  id: number;
  email: string;
  mustChangePassword: boolean;
  createdAt: string;
}

export interface RequestMetadata {
  product_count?: number | null;
  demo_products_removed?: boolean;
  store_code?: string | null;
  launch_steps?: {
    product_import?: boolean;
    yandex_contract?: boolean;
    yandex_info?: boolean;
  };
  yandex_info?: {
    city?: string;
    storeName?: string;
    managerPhone?: string;
    shopperPhone?: string;
    shopperName?: string;
    courierComments?: string;
    coordinates?: string;
    address?: string;
    shortCode?: string;
  } | null;
}

export interface AdminUserListItem {
  id: number;
  telegram_id: number;
  telegram_username?: string | null;
  telegram_first_name?: string | null;
  telegram_last_name?: string | null;
  pimmer_user_id?: string | null;
  language?: string | null;
  onboarding_completed: boolean;
  access_token_instruction_completed: boolean;
  yandex_store_instruction_completed: boolean;
  yandex_contract_confirmed: boolean;
  logo_store_name_instruction_completed: boolean;
  created_at: string;
  stores_count: number;
}

export interface AdminSubscriptionInfo {
  id: number;
  storeId: string;
  pimmerUserId?: string | null;
  planCode: 'pro_1m' | 'pro_6m' | 'pro_12m' | string;
  activatedAt: string;
  expiresAt: string;
  cancelledAt?: string | null;
  status: string;
  source: string;
  grantedBy?: string | null;
  amountKzt: number;
  notes?: string | null;
  isActive: boolean;
}

// Полные данные подписки из store_subscriptions (включая cancelled/superseded) —
// для страницы /subscriptions. Совпадает с AdminSubscriptionInfo по полям, но без `isActive`-ограничения.
export type AdminSubscriptionListItem = AdminSubscriptionInfo;

export interface AdminSubscriptionTariff {
  code: 'pro_1m' | 'pro_6m' | 'pro_12m' | string;
  title: string;
  durationMonths: number;
  priceKzt: number;
  sortOrder: number;
  isEnabled: boolean;
}

export type OrderManagementMode = 'web' | 'mobile';

export interface OrderManagementSettings {
  mode: OrderManagementMode;
  playStoreUrl: string;
  appStoreUrl: string;
}

export interface AdminStoreWithSteps {
  store_id: string;
  store_name?: string | null;
  store_address?: string | null;
  is_active: boolean;
  launch_steps: string[];
  completed_at: Record<string, string>;
  is_pro?: boolean;
  subscription?: AdminSubscriptionInfo | null;
}

export interface AdminUserDetail {
  id: number;
  telegram_id: number;
  telegram_username?: string | null;
  telegram_first_name?: string | null;
  telegram_last_name?: string | null;
  pimmer_user_id?: string | null;
  language?: string | null;
  onboarding_completed: boolean;
  access_token_instruction_completed: boolean;
  yandex_store_instruction_completed: boolean;
  yandex_contract_confirmed: boolean;
  logo_store_name_instruction_completed: boolean;
  created_at: string;
  updated_at: string;
}

export interface IntegrationRequest {
  id: number;
  store_id: string;
  store_name?: string | null;
  integration_type: string;
  pimmer_user_id: string | null;
  user_email?: string | null;
  status: string;
  rejection_comment?: string | null;
  settings?: { active?: boolean; markupPercent?: number } | null;
  request_metadata?: RequestMetadata | null;
  created_at: string;
  updated_at: string;
}

export interface VoyageSettings {
  enabled: boolean;
  useRerank: boolean;
  embedModel: string;
  rerankModel: string;
  maxHistoryMessages: number | null;
  maxContextChars: number | null;
  maxUserMessageChars: number | null;
  dialogTimeoutMs: number | null;
  retrieverTimeoutMs: number | null;
  openaiTimeoutMs: number | null;
  apiKey: string; // masked
  hasApiKey: boolean;
}

export interface VoyageSettingsForHelper extends VoyageSettings {
  apiKeyPlain?: string | null;
}

export interface NotionSettings {
  wikiPageId: string;
  wikiMaxPages: number | null;
  token: string; // masked
  hasToken: boolean;
}

export interface OpenAISettings {
  apiKey?: string;
  hasApiKey: boolean;
}

export interface PimmerSettings {
  apiBaseUrl: string;
  versionApp: string;
  lang: string;
  os: string;
  adminCabinetUrl: string;
  taskIdRemoveDemoProducts: string;
  defaultIntegrationAppId: string;
  serviceToken: string; // masked
  hasServiceToken: boolean;
}

export interface PimmerSettingsPatch {
  apiBaseUrl?: string;
  versionApp?: string;
  lang?: string;
  os?: string;
  adminCabinetUrl?: string;
  taskIdRemoveDemoProducts?: string;
  defaultIntegrationAppId?: string;
  serviceToken?: string | null;
}

export interface CatalogImportSettings {
  templateUrl: string;
  sampleUrl: string;
}

export interface FeatureFlags {
  enableTokenLogin: boolean;
  enableEmailLogin: boolean;
  showDeliveryZonesStep: boolean;
  showContactInfoStep: boolean;
  showPaymentSystemsStep: boolean;
  showEmployeesMenu: boolean;
  isDebug: boolean;
}

export interface AuthSettings {
  googleClientId: string;
  googleClientScope: string;
}

export interface WooppaySettings {
  enabled: boolean;
  merchantId: string;
  terminalId: string;
  certId: string;
  privateKey: string;
  publicKey: string;
  finalizationDelay: number;
  apiUrl: string;
  webhookBaseUrl: string;
}

export interface PaymentTransaction {
  id: number;
  storeId: string;
  planCode: string | null;
  amountKzt: number;
  status: 'pending' | 'success' | 'failed' | 'cancelled';
  wooppayTransactionId: string | null;
  paymentLink: string | null;
  errorMessage: string | null;
  initiatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export const api = {
  setToken(token: string) {
    localStorage.setItem('admin_token', token);
  },

  clearToken() {
    localStorage.removeItem('admin_token');
  },

  async login(email: string, password: string) {
    const { data } = await client.post<{
      success: boolean;
      token: string;
      admin: AdminUser;
    }>('/auth/login', { email, password });
    return data;
  },

  async getMe() {
    const { data } = await client.get<{ admin: AdminUser }>('/auth/me');
    return data;
  },

  async changePassword(email: string, password: string) {
    const { data } = await client.post<{
      success: boolean;
      token: string;
      admin: AdminUser;
    }>('/auth/change-password', { email, password });
    return data;
  },

  async getAdmins() {
    const { data } = await client.get<{ admins: AdminAccountListItem[] }>('/admins');
    return data;
  },

  async createAdmin(email: string, password: string) {
    const { data } = await client.post<{ admin: AdminAccountListItem }>('/admins', { email, password });
    return data;
  },

  async updateAdmin(id: number, payload: { email?: string; password?: string }) {
    const { data } = await client.put<{ admin: AdminAccountListItem }>(`/admins/${id}`, payload);
    return data;
  },

  async deleteAdmin(id: number) {
    const { data } = await client.delete<{ success: boolean }>(`/admins/${id}`);
    return data;
  },

  async getIntegrationRequests(params?: { type?: string; status?: string }) {
    const { data } = await client.get<{ requests: IntegrationRequest[] }>(
      '/integration-requests',
      { params }
    );
    return data;
  },

  async updateIntegrationRequestStatus(id: number, status: string, rejection_comment?: string) {
    const { data } = await client.patch<{ request: IntegrationRequest }>(
      `/integration-requests/${id}`,
      { status, rejection_comment }
    );
    return data;
  },

  async getUsers(params?: { page?: number; limit?: number; search?: string }) {
    const { data } = await client.get<{ users: AdminUserListItem[]; total: number }>('/users', {
      params: params ? { page: params.page, limit: params.limit, search: params.search } : undefined,
    });
    return data;
  },

  async getStores() {
    const { data } = await client.get<{
      stores: Array<{
        store_id: string;
        store_name?: string | null;
        pimmer_user_id?: string | null;
        user_email?: string | null;
        user_emails?: string[];
        latest_status: string;
        latest_request_at: string;
        launch_steps: string[];
        completed_at: Record<string, string>;
        is_pro?: boolean;
        subscription?: AdminSubscriptionInfo | null;
      }>;
    }>('/stores');
    return data;
  },

  async getUserDetail(id: number) {
    const { data } = await client.get<{
      user: AdminUserDetail;
      stores: AdminStoreWithSteps[];
    }>(`/users/${id}`);
    return data;
  },

  async downloadYandexInfoExcel(id: number): Promise<Blob> {
    const { data } = await client.get(`/integration-requests/${id}/yandex-info.xlsx`, {
      responseType: 'blob',
    });
    return data;
  },

  async getYandexInfoTemplateMeta(): Promise<{ exists: boolean; filename?: string; uploadedAt?: string }> {
    const { data } = await client.get('/client-settings/yandex-info-template/meta');
    return data;
  },

  async uploadYandexInfoTemplate(file: File): Promise<{ success: boolean; filename: string; uploadedAt: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await client.post('/client-settings/yandex-info-template', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  async downloadYandexInfoTemplate(): Promise<Blob> {
    const { data } = await client.get('/client-settings/yandex-info-template', {
      responseType: 'blob',
    });
    return data;
  },

  async deleteYandexInfoTemplate(): Promise<void> {
    await client.delete('/client-settings/yandex-info-template');
  },

  async getYandexInfoFields(): Promise<{ fields: Array<{ key: string; title: string; autofill?: string | null }> }> {
    const { data } = await client.get<{ fields: Array<{ key: string; title: string; autofill?: string | null }> }>(
      '/client-settings/yandex-info-fields'
    );
    return data;
  },

  async putYandexInfoFields(fields: Array<{ key: string; title: string; autofill?: string | null }>): Promise<void> {
    await client.put('/client-settings/yandex-info-fields', { fields });
  },

  async downloadDefaultYandexInfoTemplate(): Promise<Blob> {
    const { data } = await client.get('/client-settings/yandex-info-template-default', {
      responseType: 'blob',
    });
    return data;
  },

  async getYandexIntegrationGuide(): Promise<{ content: string }> {
    const { data } = await client.get<{ content: string }>('/client-settings/yandex-integration-guide');
    return data;
  },

  async putYandexIntegrationGuide(content: string): Promise<{ success: boolean }> {
    const { data } = await client.put<{ success: boolean }>('/client-settings/yandex-integration-guide', {
      content,
    });
    return data;
  },

  async getSupportChats(): Promise<{
    chats: Array<{
      id: number;
      user_id: string;
      user_name: string;
      user_email?: string | null;
      store_name?: string | null;
      last_message_at: string;
      last_message_preview: string;
    }>;
  }> {
    const { data } = await client.get<{ chats: Array<{
      id: number;
      user_id: string;
      user_name: string;
      user_email?: string | null;
      store_name?: string | null;
      last_message_at: string;
      last_message_preview: string;
    }> }>('/support-chats');
    return data;
  },

  async getSupportChatMessages(chatId: number): Promise<{
    messages: Array<{
      id: number;
      role: string;
      content: string;
      needs_manager: boolean;
      created_at: string;
    }>;
  }> {
    const { data } = await client.get<{ messages: Array<{
      id: number;
      role: string;
      content: string;
      needs_manager: boolean;
      created_at: string;
    }> }>(`/support-chats/${chatId}/messages`);
    return data;
  },

  async replyToSupportChat(chatId: number, content: string): Promise<{ ok: boolean }> {
    const { data } = await client.post<{ ok: boolean }>(`/support-chats/${chatId}/reply`, { content });
    return data;
  },

  async deleteSupportChat(chatId: number): Promise<{ success: boolean; deleted: { chat: number; messages: number } }> {
    const { data } = await client.delete<{ success: boolean; deleted: { chat: number; messages: number } }>(
      `/support-chats/${chatId}`
    );
    return data;
  },

  async getServicesStatus(): Promise<{
    admin: { status: string; timestamp: string };
    backend: { reachable: boolean; timestamp: string | null; error: string | null };
    frontendAdmin: { reachable: boolean; error: string | null };
    frontendPwa: { reachable: boolean; error: string | null };
    mysql?: { reachable: boolean; error: string | null };
    helperAi: {
      reachable: boolean;
      health: { status?: string; openai?: string; notion?: string; voyage?: string } | null;
      rag: {
        running?: boolean;
        last_pages?: number | null;
        last_chunks?: number | null;
        last_error?: string | null;
        last_started_at?: string | null;
        last_finished_at?: string | null;
      } | null;
      error: string | null;
    };
  }> {
    const { data } = await client.get<{
      admin: { status: string; timestamp: string };
      backend: { reachable: boolean; timestamp: string | null; error: string | null };
      frontendAdmin: { reachable: boolean; error: string | null };
      frontendPwa: { reachable: boolean; error: string | null };
      mysql?: { reachable: boolean; error: string | null };
      helperAi: {
        reachable: boolean;
        health: { status?: string; openai?: string; notion?: string; voyage?: string } | null;
        rag: {
          running?: boolean;
          last_pages?: number | null;
          last_chunks?: number | null;
          last_error?: string | null;
          last_started_at?: string | null;
          last_finished_at?: string | null;
        } | null;
        error: string | null;
      };
    }>('/services/status');
    return data;
  },

  async getVoyageSettings(): Promise<VoyageSettings> {
    const { data } = await client.get<VoyageSettings>('/client-settings/voyage');
    return data;
  },

  async updateVoyageSettings(
    settings: Partial<
      Omit<VoyageSettings, 'apiKey' | 'hasApiKey'> & {
        apiKey?: string | null;
      }
    >
  ): Promise<{ success: boolean; settings: VoyageSettings }> {
    const { data } = await client.put<{ success: boolean; settings: VoyageSettings }>(
      '/client-settings/voyage',
      settings
    );
    return data;
  },

  async testVoyageSettings(): Promise<{ ok: boolean }> {
    const { data } = await client.post<{ ok: boolean }>('/client-settings/voyage/test');
    return data;
  },

  async getNotionSettings(): Promise<NotionSettings> {
    const { data } = await client.get<NotionSettings>('/client-settings/notion');
    return data;
  },

  async updateNotionSettings(
    settings: Partial<{ wikiPageId: string; wikiMaxPages: number | null; token?: string | null }>
  ): Promise<{ success: boolean; settings: NotionSettings }> {
    const { data } = await client.put<{ success: boolean; settings: NotionSettings }>(
      '/client-settings/notion',
      settings
    );
    return data;
  },

  async testNotionSettings(): Promise<{ ok: boolean }> {
    const { data } = await client.post<{ ok: boolean }>('/client-settings/notion/test');
    return data;
  },

  async getOpenAISettings(): Promise<OpenAISettings> {
    const { data } = await client.get<OpenAISettings>('/client-settings/openai');
    return data;
  },

  async updateOpenAISettings(
    settings: { apiKey?: string | null }
  ): Promise<{ success: boolean; settings: OpenAISettings }> {
    const { data } = await client.put<{ success: boolean; settings: OpenAISettings }>(
      '/client-settings/openai',
      settings
    );
    return data;
  },

  async testOpenAISettings(): Promise<{ ok: boolean }> {
    const { data } = await client.post<{ ok: boolean }>('/client-settings/openai/test');
    return data;
  },

  async getPimmerSettings(): Promise<PimmerSettings> {
    const { data } = await client.get<PimmerSettings>('/client-settings/pimmer');
    return data;
  },

  async updatePimmerSettings(
    settings: PimmerSettingsPatch
  ): Promise<{ success: boolean; settings: PimmerSettings }> {
    const { data } = await client.put<{ success: boolean; settings: PimmerSettings }>(
      '/client-settings/pimmer',
      settings
    );
    return data;
  },

  async testPimmerSettings(): Promise<{ ok: boolean; status?: number; error?: string }> {
    const { data } = await client.post<{ ok: boolean; status?: number; error?: string }>(
      '/client-settings/pimmer/test'
    );
    return data;
  },

  async getCatalogImportSettings(): Promise<CatalogImportSettings> {
    const { data } = await client.get<CatalogImportSettings>('/client-settings/catalog-import');
    return data;
  },

  async updateCatalogImportSettings(
    settings: Partial<CatalogImportSettings>
  ): Promise<{ success: boolean; settings: CatalogImportSettings }> {
    const { data } = await client.put<{ success: boolean; settings: CatalogImportSettings }>(
      '/client-settings/catalog-import',
      settings
    );
    return data;
  },

  async getFeatureFlags(): Promise<FeatureFlags> {
    const { data } = await client.get<FeatureFlags>('/client-settings/feature-flags');
    return data;
  },

  async updateFeatureFlags(
    flags: Partial<FeatureFlags>
  ): Promise<{ success: boolean; settings: FeatureFlags }> {
    const { data } = await client.put<{ success: boolean; settings: FeatureFlags }>(
      '/client-settings/feature-flags',
      flags
    );
    return data;
  },

  async getAuthSettings(): Promise<AuthSettings> {
    const { data } = await client.get<AuthSettings>('/client-settings/auth');
    return data;
  },

  async updateAuthSettings(
    settings: Partial<AuthSettings>
  ): Promise<{ success: boolean; settings: AuthSettings }> {
    const { data } = await client.put<{ success: boolean; settings: AuthSettings }>(
      '/client-settings/auth',
      settings
    );
    return data;
  },

  async reindexRag(): Promise<{ started: boolean; status: object }> {
    const { data } = await client.post<{ started: boolean; status: object }>('/services/rag/reindex');
    return data;
  },

  async getSubscriptionTariffs(): Promise<{ tariffs: AdminSubscriptionTariff[] }> {
    const { data } = await client.get<{ tariffs: AdminSubscriptionTariff[] }>('/client-settings/subscription-tariffs');
    return data;
  },

  async putSubscriptionTariffs(
    tariffs: AdminSubscriptionTariff[]
  ): Promise<{ success: boolean; tariffs: AdminSubscriptionTariff[] }> {
    const { data } = await client.put<{ success: boolean; tariffs: AdminSubscriptionTariff[] }>(
      '/client-settings/subscription-tariffs',
      { tariffs }
    );
    return data;
  },

  async getOrderManagementSettings(): Promise<OrderManagementSettings> {
    const { data } = await client.get<OrderManagementSettings>('/client-settings/order-management');
    return data;
  },

  async putOrderManagementSettings(
    payload: OrderManagementSettings
  ): Promise<OrderManagementSettings> {
    const { data } = await client.put<OrderManagementSettings>(
      '/client-settings/order-management',
      payload
    );
    return data;
  },

  async getWooppaySettings(): Promise<WooppaySettings> {
    const { data } = await client.get<WooppaySettings>('/client-settings/payment');
    return data;
  },

  async updateWooppaySettings(
    settings: Partial<WooppaySettings>
  ): Promise<WooppaySettings> {
    const { data } = await client.put<WooppaySettings>('/client-settings/payment', settings);
    return data;
  },

  async getPaymentTransactions(params?: {
    limit?: number;
    offset?: number;
    status?: string;
    storeId?: string;
  }): Promise<{ transactions: PaymentTransaction[]; total: number }> {
    const { data } = await client.get<{ transactions: PaymentTransaction[]; total: number }>(
      '/admin/payment-transactions',
      { params }
    );
    return data;
  },

  async clearIntegrationRequests(confirmation: string): Promise<{ success: boolean; deleted: number }> {
    const { data } = await client.post<{ success: boolean; deleted: number }>(
      '/admin/clear/integration-requests',
      { confirmation }
    );
    return data;
  },

  async clearSubscriptions(
    confirmation: string
  ): Promise<{
    success: boolean;
    deleted: { store_subscriptions?: number; subscription_audit_log?: number };
  }> {
    const { data } = await client.post<{
      success: boolean;
      deleted: { store_subscriptions?: number; subscription_audit_log?: number };
    }>('/admin/clear/subscriptions', { confirmation });
    return data;
  },

  async getAllSubscriptions(): Promise<{ subscriptions: AdminSubscriptionListItem[] }> {
    const { data } = await client.get<{ subscriptions: AdminSubscriptionListItem[] }>('/admin/subscriptions');
    return data;
  },

  async clearUsersAndStores(
    confirmation: string
  ): Promise<{
    success: boolean;
    deleted: { users: number; user_stores: number; store_registry: number; store_launch_steps: number };
  }> {
    const { data } = await client.post<{
      success: boolean;
      deleted: { users: number; user_stores: number; store_registry: number; store_launch_steps: number };
    }>('/admin/clear/users-and-stores', { confirmation });
    return data;
  },

  async grantProSubscription(
    storeId: string,
    body: { planCode: string; durationMonths: number; notes?: string }
  ): Promise<{ subscription: AdminSubscriptionInfo }> {
    const { data } = await client.post<{ subscription: AdminSubscriptionInfo }>(
      `/admin/stores/${encodeURIComponent(storeId)}/subscription`,
      body
    );
    return data;
  },

  async cancelProSubscription(storeId: string): Promise<{ subscription: AdminSubscriptionInfo }> {
    const { data } = await client.post<{ subscription: AdminSubscriptionInfo }>(
      `/admin/stores/${encodeURIComponent(storeId)}/subscription/cancel`
    );
    return data;
  },
};
